import {
  InactiveBadge,
  NeedsResponseBadge,
} from '@/components/_domain/SidebarItemBadges/ConversationsBadge/ConversationsBadge';
import {SidebarItem} from '@/contexts/SidebarContext/SidebarContext';
import {
  CheckCircleIcon,
  InboxIcon,
  MailIcon,
  GroupsIcon as MaterialGroupsIcon,
  MobileChatIcon,
  PersonIcon,
  PersonOffIcon,
  ReplyIcon,
  SnoozeIcon,
} from '@/material-symbols/icons';
import {
  buildAdditiveFilterHref,
  deriveActiveFilters,
} from '@/pages/ENT/Conversations/List/conversationFilterBlocks';
import {
  ConversationParticipantType,
  getParticipantTypeIcon,
} from '@/components/_domain/Conversations/participantTypeIcons';
import {useSomeCommunityHasFeature} from '@ark/react';
import {useSelectedCommunities} from '@ark/web';
import {HistoryToggleOff} from '@mui/icons-material';
import {ReactNode, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

const MESSAGES_PATH = '/messages';

const SENDER_FILTER_CACHE_PREFIX = 'ark_conversationSenderFilter:';

const readCachedSenderFilter = (communityKey: string): boolean => {
  if (!communityKey) return false;
  try {
    return (
      localStorage.getItem(SENDER_FILTER_CACHE_PREFIX + communityKey) === 'true'
    );
  } catch {
    return false;
  }
};

const persistSenderFilter = (communityKey: string, value: boolean): void => {
  if (!communityKey) return;
  try {
    localStorage.setItem(
      SENDER_FILTER_CACHE_PREFIX + communityKey,
      value ? 'true' : 'false',
    );
  } catch {
    return;
  }
};

type FilterItemConfig = {
  value: string;
  label: string;
  icon: ReactNode;
  BadgeComponent?: SidebarItem['BadgeComponent'];
};

type ConversationSidebarItemsOptions = {
  includeInactive?: boolean;
};

const statusItems = (includeInactive: boolean): FilterItemConfig[] => {
  const items: FilterItemConfig[] = [
    {value: 'all', label: 'All', icon: <InboxIcon opticalSize={20} />},
    {
      value: 'needsResponse',
      label: 'Needs Response',
      icon: <ReplyIcon opticalSize={20} />,
      BadgeComponent: NeedsResponseBadge,
    },
    {value: 'snoozed', label: 'Snoozed', icon: <SnoozeIcon opticalSize={20} />},
    {
      value: 'closed',
      label: 'Closed',
      icon: <CheckCircleIcon opticalSize={20} />,
    },
  ];
  if (includeInactive) {
    items.push({
      value: 'inactive',
      label: 'Inactive',
      icon: <HistoryToggleOff fontSize="small" />,
      BadgeComponent: InactiveBadge,
    });
  }
  return items;
};

const assignmentItems: FilterItemConfig[] = [
  {
    value: 'assigned',
    label: 'Assigned to Me',
    icon: <PersonIcon opticalSize={20} />,
  },
  {
    value: 'assignedToMyTeam',
    label: 'My Teams',
    icon: <MaterialGroupsIcon opticalSize={20} />,
  },
  {
    value: 'unassigned',
    label: 'Unassigned',
    icon: <PersonOffIcon opticalSize={20} />,
  },
];

const sourceItems: FilterItemConfig[] = [
  {
    value: 'fromEmail',
    label: 'From Email',
    icon: <MailIcon opticalSize={20} />,
  },
  {
    value: 'fromArk',
    label: 'From App',
    icon: <MobileChatIcon opticalSize={20} />,
  },
];

const SENDER_FILTER_TYPES: {
  value: string;
  label: string;
  type: ConversationParticipantType;
}[] = [
  {value: 'resident', label: 'Resident', type: 'RESIDENT'},
  {value: 'leaseholder', label: 'Leaseholder', type: 'LEASEHOLDER'},
  {value: 'contractor', label: 'Contractor', type: 'CONTRACTOR'},
  {value: 'internal', label: 'Internal', type: 'INTERNAL'},
  {value: 'unknown', label: 'Unknown', type: 'UNKNOWN'},
];

const senderItems: FilterItemConfig[] = SENDER_FILTER_TYPES.map(
  ({value, label, type}) => {
    const Icon = getParticipantTypeIcon(type);
    return {value, label, icon: <Icon fontSize="small" />};
  },
);

export const useConversationSidebarItems = (
  options: ConversationSidebarItemsOptions = {},
): SidebarItem[] => {
  const {includeInactive = false} = options;
  const [searchParams] = useSearchParams();
  const communities = useSelectedCommunities();
  const communityKey = [...communities].sort().join(',');
  const {hasFeature, loading} = useSomeCommunityHasFeature(
    communities,
    'ConversationSenderFilter',
  );

  const [hasSenderFilter, setHasSenderFilter] = useState(() =>
    readCachedSenderFilter(communityKey),
  );

  useEffect(() => {
    if (loading || !communityKey || hasFeature === undefined) {
      setHasSenderFilter(readCachedSenderFilter(communityKey));
      return;
    }
    setHasSenderFilter(hasFeature);
    persistSenderFilter(communityKey, hasFeature);
  }, [loading, hasFeature, communityKey]);

  return useMemo(() => {
    const activeFilters = deriveActiveFilters(searchParams.getAll('filter'));

    const toItem = (
      config: FilterItemConfig,
      blockId: keyof typeof activeFilters,
    ): SidebarItem => ({
      id: `${blockId}:${config.value}`,
      url: buildAdditiveFilterHref(MESSAGES_PATH, searchParams, config.value),
      label: config.label,
      icon: config.icon,
      BadgeComponent: config.BadgeComponent,
      isActive: activeFilters[blockId] === config.value,
    });

    const leadBlock = (
      configs: FilterItemConfig[],
      blockId: keyof typeof activeFilters,
      sectionTitle: string,
    ): SidebarItem[] =>
      configs.map((config, index) => ({
        ...toItem(config, blockId),
        sectionTitle: index === 0 ? sectionTitle : undefined,
      }));

    const dividedBlock = (
      configs: FilterItemConfig[],
      blockId: keyof typeof activeFilters,
      sectionTitle: string,
    ): SidebarItem[] => [
      {url: '', label: '', icon: null, type: 'divider', sectionTitle},
      ...configs.map(config => toItem(config, blockId)),
    ];

    const items: SidebarItem[] = [
      ...leadBlock(statusItems(includeInactive), 'status', 'Status'),
      ...dividedBlock(assignmentItems, 'assignment', 'Assignment'),
      ...dividedBlock(sourceItems, 'source', 'Source'),
    ];
    if (hasSenderFilter) {
      items.push(...dividedBlock(senderItems, 'sender', 'Sender'));
    }

    return items;
  }, [searchParams, hasSenderFilter, includeInactive]);
};
